import React, { useEffect, useState } from "react";
import { FiXCircle } from "react-icons/fi";
import { validateManualQuestWithAI } from "../api/openai";
import { playSystemSound } from "../audio/soundManager";
import {
  QUEST_TYPES,
  formatQuestDuration,
  formatQuestTime,
  normalizeQuestDuration,
  normalizeQuestTime,
  normalizeQuestType,
} from "../utils/questUtils";

const DEFAULT = {
  title: "",
  desc: "",
  xpHint: 30,
  questType: QUEST_TYPES.STANDARD,
  deadlineTime: "",
  durationMinutes: "",
};

const buildDraftState = (source) => ({
  title: source?.title || "",
  desc: source?.desc || "",
  xpHint: Number(source?.xp ?? source?.xpHint ?? 30),
  questType: normalizeQuestType(source?.questType),
  deadlineTime: source?.deadlineTime || "",
  durationMinutes: source?.durationMinutes ?? "",
});

const buildReviewedPayload = (draft, review, initialData) => ({
  ...(initialData || {}),
  title: review.title,
  desc: review.desc,
  xp: review.xp,
  penaltyQuest: review.penaltyQuest,
  acceptanceCriteria: review.acceptanceCriteria,
  fixed: !!initialData?.fixed,
  questType: normalizeQuestType(draft.questType),
  deadlineTime: normalizeQuestTime(draft.deadlineTime),
  durationMinutes: normalizeQuestDuration(draft.durationMinutes),
});

const AddQuestModal = ({
  show,
  userLevel = 1,
  onClose,
  onAdd,
  initialData,
  isEdit,
}) => {
  const [form, setForm] = useState(buildDraftState(initialData || DEFAULT));
  const [review, setReview] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    setForm(buildDraftState(initialData || DEFAULT));
    setReview(null);
    setIsValidating(false);
    setIsConfirming(false);
    setFeedback(null);
  }, [initialData, show]);

  useEffect(() => {
    if (show) {
      playSystemSound("questOpen");
    }
  }, [show]);

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (feedback) setFeedback(null);
    if (review) setReview(null);
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateDraft = () => {
    if (!form.title.trim()) {
      return {
        tone: "warning",
        label: "[SYSTEM NOTICE]",
        text: "Enter a quest title before issuing a new order.",
      };
    }

    if (
      normalizeQuestType(form.questType) === QUEST_TYPES.DEADLINE &&
      !normalizeQuestTime(form.deadlineTime)
    ) {
      return {
        tone: "warning",
        label: "[SYSTEM NOTICE]",
        text: "Deadline quests need a completion cutoff time.",
      };
    }

    if (
      normalizeQuestType(form.questType) === QUEST_TYPES.DURATION &&
      !normalizeQuestDuration(form.durationMinutes)
    ) {
      return {
        tone: "warning",
        label: "[SYSTEM NOTICE]",
        text: "Duration quests need a timer length in minutes.",
      };
    }

    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const localError = validateDraft();
    if (localError) {
      setFeedback(localError);
      return;
    }

    setIsValidating(true);
    setFeedback(null);
    setReview(null);
    playSystemSound("questAction");

    try {
      const result = await validateManualQuestWithAI({
        title: form.title.trim(),
        desc: (form.desc || "").trim(),
        xpHint: Number(form.xpHint) || 0,
        questType: normalizeQuestType(form.questType),
        deadlineTime: normalizeQuestTime(form.deadlineTime),
        durationMinutes: normalizeQuestDuration(form.durationMinutes),
        fixed: !!initialData?.fixed,
        userLevel,
      });

      if (!result.valid) {
        setFeedback({
          tone: "warning",
          label: "[SYSTEM REJECTION]",
          text: result.suggestion
            ? `${result.error} ${result.suggestion}`
            : result.error,
        });
        return;
      }

      setReview({
        ...result,
        payload: buildReviewedPayload(form, result, initialData),
      });
    } catch (error) {
      console.error("Manual quest validation failed:", error);
      setFeedback({
        tone: "error",
        label: "[SYSTEM ERROR]",
        text:
          error?.message ||
          "The System could not validate this quest. Try again.",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfirm = async () => {
    if (!review?.payload) return;

    setIsConfirming(true);
    setFeedback(null);
    playSystemSound("questAction");

    try {
      await Promise.resolve(onAdd(review.payload));
      onClose();
    } catch (error) {
      console.error("Saving reviewed quest failed:", error);
      setFeedback({
        tone: "error",
        label: "[SYSTEM ERROR]",
        text: error?.message || "Quest save failed after review. Try again.",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  if (!show) return null;

  const questType = normalizeQuestType(form.questType);
  const reviewedQuest = review?.payload || null;

  return (
    <div className="solo-modal-bg" onClick={onClose}>
      <form
        className="solo-modal add-quest-modal"
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="solo-close"
          title="Close"
          onClick={onClose}
          aria-label="Close"
        >
          <FiXCircle />
        </button>

        <div className="add-quest-modal__header">
          <h2>{isEdit ? "Edit Quest" : "Add New Quest"}</h2>
          {reviewedQuest && (
            <p className="add-quest-modal__step">System review complete</p>
          )}
        </div>

        {feedback && (
          <div className={`system-feedback system-feedback--${feedback.tone}`}>
            <div className="system-feedback__label">{feedback.label}</div>
            <div className="system-feedback__text">{feedback.text}</div>
          </div>
        )}

        {!reviewedQuest ? (
          <>
            <div className="solo-field">
              <label htmlFor="title">Quest Title</label>
              <input
                id="title"
                name="title"
                placeholder="e.g. Study module 2 for 90 minutes"
                value={form.title}
                onChange={onChange}
                required
              />
            </div>

            <div className="solo-field">
              <label htmlFor="desc">Description</label>
              <textarea
                id="desc"
                name="desc"
                rows={3}
                placeholder="Add the real-world context so the System can tighten the order."
                value={form.desc}
                onChange={onChange}
              />
            </div>

            <div className="add-quest-modal__meta">
              <div className="solo-field add-quest-modal__xp">
                <label htmlFor="xpHint">XP hint</label>
                <input
                  id="xpHint"
                  name="xpHint"
                  type="number"
                  min={10}
                  step={5}
                  value={form.xpHint}
                  onChange={(event) => {
                    if (feedback) setFeedback(null);
                    if (review) setReview(null);
                    setForm((current) => ({
                      ...current,
                      xpHint: Number(event.target.value),
                    }));
                  }}
                  required
                />
              </div>

              <div className="solo-field">
                <label htmlFor="questType">Quest format</label>
                <select
                  id="questType"
                  name="questType"
                  value={form.questType || QUEST_TYPES.STANDARD}
                  onChange={onChange}
                >
                  <option value={QUEST_TYPES.STANDARD}>Standard quest</option>
                  <option value={QUEST_TYPES.DEADLINE}>Deadline quest</option>
                  <option value={QUEST_TYPES.DURATION}>Duration quest</option>
                </select>
              </div>
            </div>

            <div className="add-quest-modal__rules">
              {questType === QUEST_TYPES.DEADLINE && (
                <div className="solo-field">
                  <label htmlFor="deadlineTime">Complete by</label>
                  <input
                    id="deadlineTime"
                    name="deadlineTime"
                    type="time"
                    value={form.deadlineTime || ""}
                    onChange={onChange}
                  />
                </div>
              )}

              {questType === QUEST_TYPES.DURATION && (
                <>
                  <div className="solo-field">
                    <label htmlFor="durationMinutes">Timer minutes</label>
                    <input
                      id="durationMinutes"
                      name="durationMinutes"
                      type="number"
                      min={15}
                      max={360}
                      step={15}
                      value={form.durationMinutes || ""}
                      onChange={onChange}
                      placeholder="e.g. 120"
                    />
                  </div>

                  <div className="solo-field">
                    <label htmlFor="deadlineTime">Optional finish-by time</label>
                    <input
                      id="deadlineTime"
                      name="deadlineTime"
                      type="time"
                      value={form.deadlineTime || ""}
                      onChange={onChange}
                    />
                  </div>
                </>
              )}
            </div>

            <p className="add-quest-modal__hint">
              Use a deadline quest for cutoffs like wake by 7:00 AM. Use a duration quest for timed blocks like 120 minutes of study. Duration quests must be started from the quest board before the System timer begins. Overnight cutoffs like 12:00 AM or 1:00 AM are treated as the end of your current quest day, not an instant post-midnight failure. Recurring status is assigned from the quest board after the order is created. The System will decide the final XP, penalty, and completion rule.
            </p>

            <div className="solo-actions add-quest-modal__actions">
              <button
                type="button"
                className="logout-btn add-quest-modal__action-btn"
                onClick={onClose}
                disabled={isValidating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="login-btn add-quest-modal__action-btn"
                disabled={isValidating}
              >
                {isValidating ? "System Reviewing..." : "Request System Review"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="add-quest-modal__review">
              {review.reviewNote && (
                <div className="system-feedback system-feedback--info">
                  <div className="system-feedback__label">[SYSTEM REVIEW]</div>
                  <div className="system-feedback__text">{review.reviewNote}</div>
                </div>
              )}

              <div className="add-quest-modal__review-grid">
                <div className="add-quest-modal__review-card add-quest-modal__review-card--wide">
                  <div className="add-quest-modal__review-label">Title</div>
                  <div className="add-quest-modal__review-value">
                    {reviewedQuest.title}
                  </div>
                </div>

                <div className="add-quest-modal__review-card add-quest-modal__review-card--wide">
                  <div className="add-quest-modal__review-label">Description</div>
                  <div className="add-quest-modal__review-value">
                    {reviewedQuest.desc || "No description provided."}
                  </div>
                </div>

                <div className="add-quest-modal__review-card">
                  <div className="add-quest-modal__review-label">Final XP</div>
                  <div className="add-quest-modal__review-value">
                    {reviewedQuest.xp}
                  </div>
                </div>

                <div className="add-quest-modal__review-card">
                  <div className="add-quest-modal__review-label">Quest format</div>
                  <div className="add-quest-modal__review-value">
                    {questType === QUEST_TYPES.DEADLINE
                      ? "Deadline quest"
                      : questType === QUEST_TYPES.DURATION
                        ? "Duration quest"
                        : "Standard quest"}
                  </div>
                </div>

                {reviewedQuest.deadlineTime && (
                  <div className="add-quest-modal__review-card">
                    <div className="add-quest-modal__review-label">Deadline</div>
                    <div className="add-quest-modal__review-value">
                      {formatQuestTime(reviewedQuest.deadlineTime)}
                    </div>
                  </div>
                )}

                {reviewedQuest.durationMinutes && (
                  <div className="add-quest-modal__review-card">
                    <div className="add-quest-modal__review-label">Duration</div>
                    <div className="add-quest-modal__review-value">
                      {formatQuestDuration(reviewedQuest.durationMinutes)}
                    </div>
                  </div>
                )}

                {questType === QUEST_TYPES.DURATION && (
                  <div className="add-quest-modal__review-card add-quest-modal__review-card--wide">
                    <div className="add-quest-modal__review-label">System timer flow</div>
                    <div className="add-quest-modal__review-value">
                      Start this quest from the board. The timer runs from that moment and completion unlocks only after the full duration has elapsed.
                    </div>
                  </div>
                )}

                <div className="add-quest-modal__review-card add-quest-modal__review-card--wide">
                  <div className="add-quest-modal__review-label">Complete when</div>
                  <div className="add-quest-modal__review-value">
                    {reviewedQuest.acceptanceCriteria}
                  </div>
                </div>

                <div className="add-quest-modal__review-card add-quest-modal__review-card--wide">
                  <div className="add-quest-modal__review-label">Penalty quest</div>
                  <div className="add-quest-modal__review-value">
                    {reviewedQuest.penaltyQuest}
                  </div>
                </div>
              </div>
            </div>

            <div className="solo-actions add-quest-modal__actions">
              <button
                type="button"
                className="logout-btn add-quest-modal__action-btn"
                onClick={() => {
                  setReview(null);
                  setFeedback(null);
                }}
                disabled={isConfirming}
              >
                Back to Draft
              </button>
              <button
                type="button"
                className="login-btn add-quest-modal__action-btn"
                onClick={handleConfirm}
                disabled={isConfirming}
              >
                {isConfirming
                  ? "Saving..."
                  : isEdit
                    ? "Confirm Save"
                    : "Confirm Add"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default AddQuestModal;
